<template>
  <lf-input
    ref="inputRef"
    :value="effectiveValue"
    :placeholder="placeholder"
    :name="name"
    @click="handleClick"
  >
    <template #suffix>
      <slot name="suffix" />
    </template>
  </lf-input>
</template>

<script setup lang="ts">
import { computed, nextTick, watch } from "vue";
import { useCurrencyInput } from "vue-currency-input";

import debounce from "lodash/debounce";

import type { CurrencyInputOptions } from "vue-currency-input";

const {
  modelValue,
  options = {},
  placeholder,
  name,
  defaultValue,
} = defineProps<{
  modelValue?: number | string;
  options?: CurrencyInputOptions;
  placeholder?: string;
  name: string;
  defaultValue?: number;
}>();

const DEFAULT_OPTIONS = {
  currency: "USD",
  locale: "en",
  distractionFree: true,
  useGrouping: true,
  precision: { min: 0, max: 3 },
  valueRange: { min: 0, max: 999999999 },
} as const;

const effectiveOptions = computed(() => {
  return {
    ...DEFAULT_OPTIONS,
    ...options,
  };
});

const { inputRef, setOptions, setValue } = useCurrencyInput(
  effectiveOptions.value
);

const effectiveValue = computed(() => modelValue ?? defaultValue);

const debouncedSetValue = debounce((value) => {
  setValue(value);
}, 100);

// Handle click to fix cursor position issue
const handleClick = () => {
  // Use nextTick to ensure DOM is updated before manipulating the cursor
  nextTick(() => {
    if (!inputRef.value) {
      return;
    }

    // Get the actual input element
    const input = inputRef.value.$el.querySelector("input");
    if (!input) {
      return;
    }

    // Move cursor to the end of the input
    const length = input.value.length;
    input.setSelectionRange(length, length);
  });
};

watch(
  () => modelValue,
  () => {
    const value = parseFloat(`${effectiveValue.value}`);
    debouncedSetValue(value);
  }
);

watch(
  () => options,
  () => {
    setOptions(effectiveOptions.value);
  },
  { flush: "post" }
);
</script>
